import { doc, updateDoc } from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'

export type RetryWhatsappRelayInput = {
  workspaceId: string
  channelId: string
  messageId: string
}

export type RetryWhatsappRelayResult = {
  success: boolean
  status?: string
}

export type WhatsappRoute = 'TIMELINING' | 'OWN'
export const VALID_WHATSAPP_AUDIENCES = [
  'HELPER',
  'FIELD_PASS',
  'GUARDIAN',
  'CLIENT',
  'VENDOR',
] as const
export type WhatsappAudienceKind = (typeof VALID_WHATSAPP_AUDIENCES)[number]

export type WhatsappSettingsPatch = {
  enabled?: boolean
  route?: WhatsappRoute
  audiences?: WhatsappAudienceKind[]
  notify?: {
    education?: boolean
    helperJobs?: boolean
    approvals?: boolean
    adminSend?: boolean
    bookings?: boolean
  }
  pushGraceMinutes?: number
}

export type UpdateWhatsappSettingsInput = {
  workspaceId: string
  patch: WhatsappSettingsPatch
}

export type UpdateWhatsappSettingsResult = {
  ok: true
}

export type GetWhatsappUsageInput = {
  workspaceId: string
  month?: string
}

export type GetWhatsappUsageResult = {
  month: string
  sentOnTimelining: number
  pendingCount: number
  paused: boolean
  cap: number
  creditsCharged: number
}

export const retryWhatsappMessage = async (
  input: RetryWhatsappRelayInput,
): Promise<RetryWhatsappRelayResult> => {
  return callable<RetryWhatsappRelayInput, RetryWhatsappRelayResult>(
    'retryWhatsappRelay',
    input,
  )
}

export const updateWhatsappSettings = async (
  input: UpdateWhatsappSettingsInput,
): Promise<UpdateWhatsappSettingsResult> => {
  return callable<UpdateWhatsappSettingsInput, UpdateWhatsappSettingsResult>(
    'updateWhatsappSettings',
    input,
  )
}

export const getWhatsappUsage = async (
  input: GetWhatsappUsageInput,
): Promise<GetWhatsappUsageResult> => {
  return callable<GetWhatsappUsageInput, GetWhatsappUsageResult>(
    'getWhatsappUsage',
    input,
  )
}

export type GetWhatsappInviteInfoInput = {
  workspaceId: string
}

export type GetWhatsappInviteInfoResult = {
  enabled: boolean
  sendingNumberE164: string | null
}

export const getWhatsappInviteInfo = async (
  workspaceId: string,
): Promise<GetWhatsappInviteInfoResult> => {
  return callable<GetWhatsappInviteInfoInput, GetWhatsappInviteInfoResult>(
    'getWhatsappInviteInfo',
    { workspaceId },
  )
}

export type UpdateWhatsappCommandOutcomeInput = {
  workspaceId: string
  channelId: string
  messageId: string
  outcome: 'ACCEPTED' | 'SENT_BACK' | 'OPENED'
}

export const updateWhatsappCommandOutcome = async ({
  workspaceId,
  channelId,
  messageId,
  outcome,
}: UpdateWhatsappCommandOutcomeInput): Promise<void> => {
  const msgRef = doc(firestoreDb, 'workspaces', workspaceId, 'channels', channelId, 'messages', messageId)
  await updateDoc(msgRef, {
    'metadata.whatsapp.command.outcome': outcome,
  })
}

